/**
 * AiPanelEV — Expert View AI chat panel (purview-design)
 *
 * Five states (Figma node 617-17048):
 *   loading   → spinner + "Loading. Please wait."
 *   error     → warning icon + error message
 *   initial   → "Ask AI" title + AiChatInput + prompt suggestions
 *   answering → chat log + user bubble + "Thinking…" dots
 *   answered  → chat log + full answer + "Done thinking." + rating row
 *
 * Self-managing: starts in 'loading', auto-advances to 'initial' after 1.5s.
 * Submitting a message advances to 'answering' → 'answered' (mock, 2s delay).
 * All colors are hex literals — no getColor() / theme provider required.
 */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled, { keyframes, ThemeContext } from 'styled-components';
import { Icon } from '../elements/Icon';
import { Anchor } from '../elements/Anchor';
import { Tooltip } from '../elements/Tooltip';
import { AiChatInput } from './AiChatInput';
import { AiSourceLink } from './AiSourceLink';
import { RecordFileBadge } from './RecordFileBadge';
import type { AiSourceEntry } from './AiSourceLink';
import type { RecordClass } from '../utils/recordFileIcon';
import { ICON_ERROR_HEX, ICON_HAPPY, ICON_SAD, ICON_STARS_CIRCLE, ICON_THUMBS_DOWN, ICON_THUMBS_UP } from '../refs/Icons';
// import { ICON_ERROR_HEX } from '@/refs/Icons';

// ─── Color tokens (hex literals — no theme required) ──────────────────────────
const HEX_WHITE              = '#ffffff';
const HEX_LOCHMARA           = '#008dd0';
const HEX_GRAY_60            = '#b3b3b3';
const C_ASIDE_BG             = '#EFF4F7';
const C_ICON_BTN_BG          = '#DFEDF4';
const C_HISTORY_TOPIC_LINK   = '#007DB8';
const C_HISTORY_TOPIC_DESC   = '#5A5A5A';
const C_HISTORY_TOPIC_TIME   = '#999999';
const C_HISTORY_TOPIC_BORDER = 'rgba(0,0,0,0.08)';
const C_PROMPT_BG            = '#F6FBFE';
const C_TITLE_TEXT           = '#5A5A5A';
const C_GRAY_ICON            = '#999999';
const C_LOADING_TEXT         = '#787878';
const C_ANSWER_TEXT          = '#444444';
const C_SCROLL_THUMB         = '#E5E5E5';

// ─── Theme-aware helpers ───────────────────────────────────────────────────────
// t() lets styled components fall back to a constant when no theme is provided.
type AnyProps = { theme?: { colors?: Record<string, string> } };
const t = (token: string, fallback: string) => (props: AnyProps) =>
  props.theme?.colors?.[token] ?? fallback;

// Hook for reading AI colors inside component render functions (JSX props).
const useAiColors = () => {
  const theme = useContext(ThemeContext) as any;
  const c = theme?.colors ?? {};
  return {
    main:      c.aiPanelMainColor ?? HEX_LOCHMARA,
    shadow:    c.aiPanelShadow    ?? '#0072a9',
    iconBtnBg: c.aiPanelIconBtnBg ?? C_ICON_BTN_BG,
    linkColor: c.aiLinkColor      ?? C_HISTORY_TOPIC_LINK,
    bubbleBg:  c.aiChatBubbleBg   ?? C_PROMPT_BG,
    panelBg:   c.aiPanelBg        ?? C_ASIDE_BG,
  };
};

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_RECORD_FILE = 'CT, Head or Brain w/o Contrast';

// ─── Mock answer: structured paragraphs with optional inline source links ─────
type AnswerParagraph = {
  text: string;
  sources?: AiSourceLink[];
};

const MOCK_ANSWER_PARTS: AnswerParagraph[] = [
  {
    text: 'Summary of Key Record Content',
  },
  {
    text: '1. The "Frequency-Pain" Correlation\nEarly records highlight a direct mathematical link between decibel levels and perceived physical pressure. This led to the discovery that the patient\u2019s brain was treating sound waves as physical projectiles, a phenomenon the specialists labeled "Sonic Percussion Syndrome."',
    sources: [
      {
        label: 'Med log',
        sources: [
          {
            filename: 'Medication log',
            excerpt: 'Trial of standard GABA-inhibitors. Patient reported the pill caused an auditory shimmer effect within 40 minutes of ingestion.',
            url: 'https://example.com/medication-log',
          },
          {
            filename: 'Medication log',
            excerpt: 'Dosage adjusted to 150 mg twice daily. No significant change in the frequency-pain correlation was observed during this period.',
            url: 'https://example.com/medication-log',
          },
        ],
      },
    ],
  },
  {
    text: '2. The Failure of Traditional Chemistry\nThe Pharmacy logs are particularly grim. Because the patient\u2019s neurotransmitters are "drifting," standard medications metabolize unpredictably, creating a feedback loop the team struggled to break.',
    sources: [
      {
        label: 'Pharmacy log',
        sources: [
          {
            filename: 'Pharmacy log',
            excerpt: 'Neurotransmitter drift confirmed — standard medications metabolize unpredictably, creating a compounding feedback loop.',
            url: 'https://example.com/pharmacy-log',
          },
        ],
      },
    ],
  },
  {
    text: '3. The Visual-Olfactory Crossover\nLater entries document an unexpected side effect: the patient began perceiving certain smells as colors. The team noted a significant increase in hyper-connectivity between the auditory and somatosensory cortex.',
    sources: [
      {
        label: 'CT scan',
        sources: [
          {
            filename: 'CT Head w/o Contrast',
            excerpt: 'Increased hyper-connectivity between the auditory and somatosensory cortex confirmed on comparative axial sections.',
            url: 'https://example.com/ct-report',
          },
        ],
      },
    ],
  },
];

// Flat string version used for history messages and answering stream simulation
const MOCK_ANSWER = MOCK_ANSWER_PARTS.map(p => p.text).join('\n\n');

// ─── Types ────────────────────────────────────────────────────────────────────
export type AiPanelEVState = 'loading' | 'error' | 'initial' | 'answering' | 'answered';

export type AiHistoryTopic = {
  id: string;
  title: string;
  preview: string;
  timestamp: string;
};

export type AiChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

export type AiSourceLink = {
  label: string;
  sources: AiSourceEntry[];
};

export type AiChatSource = {
  n: number;
  documentId?: string;
  chunkIndex?: number;
  pages?: number[];
  sourceLocation?: string;
  score?: number;
  chunk?: string;
  kind?: string;
  pictureRef?: string;
  pictureLabel?: string;
  selfRefs?: string[];
  chunkId?: string;
  sourceText?: string;
  /** Resolved blob URL for IMAGE kind sources (set after fetching) */
  imageUrl?: string;
};

export type AiPanelEVProps = {
  /** Override starting state (defaults to 'loading' → auto-advances to 'initial') */
  initialState?: AiPanelEVState;
  /** Record file name shown in the chat input badge */
  recordFileName?: string;
  /** Record class for icon resolution (e.g. 'Pathology', 'Radiology') */
  recordClass?: RecordClass;
  /** Explicit file extension override for icon resolution */
  fileExtension?: string;
  /** Called when the close button is clicked */
  onClose?: () => void;
  /** Send a chat message via streaming. Call onChunk with each text fragment; call onDone when finished. */
  onSendMessage?: (question: string, onChunk: (text: string) => void, onDone: () => void, onError: () => void, onSources: (sources: AiChatSource[]) => void) => void;
  /** Called when user clicks a source link — receives (page, highlightText) */
  onOpenSource?: (page: number | null, highlightText: string | null) => void;
  /** Status label shown while document is still being processed (disables input) */
  processingLabel?: string | null;
  /** Hides all AI feedback prompts and modal when true. */
  hideFeedbacks?: boolean;
  className?: string;
};

// ─── Animations ───────────────────────────────────────────────────────────────
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
  40%            { opacity: 1;   transform: scale(1);   }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Root ─────────────────────────────────────────────────────────────────────
const Root = styled.div`
  display: flex;
  width: 386px;
  height: 717px;
  background: ${HEX_WHITE};
  font-family: 'Open Sans', sans-serif;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
`;

// ─── Close button ─────────────────────────────────────────────────────────────
const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 5px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  box-sizing: border-box;
`;

const CloseButtonTrigger = styled(Tooltip.Trigger)<{ $asideVisible?: boolean }>`
  position: absolute;
  top: 16px;
  right: ${({ $asideVisible }) => ($asideVisible ? '80px' : '16px')};
  z-index: 5;
`;

// ─── Main column ──────────────────────────────────────────────────────────────
const Main = styled.div<{ $chatMode: boolean, $paddingTop?: string }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  padding: ${({ $chatMode }) => ($chatMode ? '24px 0 0 24px' : '24px 24px 24px 24px')};
  gap: ${({ $chatMode }) => ($chatMode ? '16px' : '40px')};
  padding-top: ${({ $paddingTop }) => ($paddingTop ? $paddingTop : '240px')};
`;

// ─── Title ("Ask AI") ─────────────────────────────────────────────────────────
const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  animation: ${fadeSlideIn} 0.25s ease both;
`;

const TitleText = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${C_TITLE_TEXT};
  line-height: 1;
`;

// ─── Content area (initial state) ─────────────────────────────────────────────
const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  min-height: 0;
  animation: ${fadeSlideIn} 0.25s ease both;
`;

// ─── Loading state ─────────────────────────────────────────────────────────────
const StatusWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  animation: ${fadeSlideIn} 0.25s ease both;
  align-items: center;
  justify-content: center;
  align-content: center;
`;

const SpinnerRing = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid ${t('aiPanelIconBtnBg', C_ICON_BTN_BG)};
  border-top-color: ${t('aiPanelMainColor', HEX_LOCHMARA)};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const StatusText = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${C_LOADING_TEXT};
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

// ─── Chat layout ──────────────────────────────────────────────────────────────
const ChatContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ChatScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 24px 16px 0;
  scrollbar-width: thin;
  scrollbar-color: ${C_SCROLL_THUMB} transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${C_SCROLL_THUMB}; border-radius: 2px; }
`;

const TopFade = styled.div<{ $visible: boolean }>`
  position: sticky;
  top: -8px;
  height: 48px;
  margin-bottom: -48px;
  background: linear-gradient(to bottom, ${HEX_WHITE}, rgba(255,255,255,0));
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s;
  z-index: 1;
  flex-shrink: 0;
`;

const BottomFade = styled.div<{ $visible: boolean }>`
  height: 48px;
  margin-top: -48px;
  margin-right: 24px;
  background: linear-gradient(to top, ${HEX_WHITE}, rgba(255,255,255,0));
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
`;

const ChatInputWrap = styled.div`
  flex-shrink: 0;
  padding-right: 24px;
  padding-bottom: 16px;
`;


// ─── Chat messages ────────────────────────────────────────────────────────────
const PromptWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const PromptBubble = styled.div`
  max-width: 248px;
  padding: 16px;
  background: ${t('aiChatBubbleBg', C_PROMPT_BG)};
  border: 1px solid ${t('aiPanelBg', C_ASIDE_BG)};
  border-radius: 10px;
  font-size: 12px;
  font-weight: 400;
  color: ${C_HISTORY_TOPIC_DESC};
  line-height: 1.5;
  animation: ${fadeSlideIn} 0.2s ease both;
`;

const AnswerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  animation: ${fadeSlideIn} 0.2s ease both;
`;

const AiStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AiStatusText = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: ${C_GRAY_ICON};
  line-height: 1.4;
`;

// Paragraph wrapper for answer parts
// Using inline-block on the paragraph allows the chip to sit flush after the text.
const AnswerPara = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: ${C_ANSWER_TEXT};
  line-height: 1.7;
  white-space: pre-wrap;
`;

const AnswerParasWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

// ─── Thinking dots ────────────────────────────────────────────────────────────
const DotsWrap = styled.span`
  display: inline-flex;
  gap: 3px;
  align-items: center;
  vertical-align: middle;
  margin-left: 2px;
`;

const Dot = styled.span<{ $delay: string }>`
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: ${C_GRAY_ICON};
  animation: ${dotPulse} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay};
`;

const ThinkingDots: React.FC = () => (
  <DotsWrap>
    <Dot $delay="0s" />
    <Dot $delay="0.2s" />
    <Dot $delay="0.4s" />
  </DotsWrap>
);

// ─── Rating row ───────────────────────────────────────────────────────────────
const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RatingLabel = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: ${C_GRAY_ICON};
`;

const ThumbsRow = styled.div`
  display: flex;
  gap: 4px;
`;

const ThumbBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 2px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
  color: ${HEX_GRAY_60};
  transition: background 0.15s, color 0.15s;
`;

// ─── Feedback panel ───────────────────────────────────────────────────────────
export type FeedbackPanelState = 'closed' | 'open' | 'sending' | 'success' | 'error';

const FeedbackOverlay = styled.div`
  position: absolute;
  bottom: 160px;
  left: 24px;
  width: 302px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  z-index: 10;
  font-family: 'Open Sans', sans-serif;
  animation: ${fadeSlideIn} 0.2s ease both;
`;

const FeedbackButtonsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FeedbackForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  position: relative;
  ${FeedbackButtonsRow} {
    justify-content: space-between;
  }
`;

const FeedbackResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 18px;
  padding: 72px 24px 32px 24px;
  position: relative;
`;

const FeedbackLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #444444;
  margin: 0;
  line-height: 1.5;
`;

const FeedbackTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 8px 13px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #444444;
  resize: vertical;
  box-sizing: border-box;
  outline: none;
  line-height: 1.5;
  &::placeholder { color: #b3b3b3; }
  &:focus { border-color: ${t('aiPanelMainColor', '#008dd0')}; }
`;

const FeedbackCancelBtn = styled.button`
  background: transparent;
  border: none;
  padding: 4px 8px;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${t('aiLinkColor', '#007ab9')};
  cursor: pointer;
  border-radius: 4px;
  &:hover { background: ${t('aiPanelIconBtnBg', C_ICON_BTN_BG)}; }
`;

const FeedbackPrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${t('aiPanelMainColor', '#008dd0')};
  border: 1px solid ${t('aiPanelMainColor', '#008dd0')};
  border-radius: 5px;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${t('aiPanelShadow', '#0072a9')}; border-color: ${t('aiPanelShadow', '#0072a9')}; }
  &:disabled { opacity: 0.65; cursor: default; }
`;

const FeedbackGhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${t('aiLinkColor', '#007ab9')};
  cursor: pointer;
  &:hover { background: ${t('aiPanelIconBtnBg', C_ICON_BTN_BG)}; }
`;

const FeedbackCloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.05); }
`;

const FeedbackTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const FeedbackResultTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #444444;
  margin: 0;
  line-height: 1.4;
`;

// Spinner ring for sending state
const FeedbackSpinner = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;


export type FeedbackPanelProps = {
  rating: 'up' | 'down' | null;
  state: FeedbackPanelState;
  text: string;
  onTextChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onClose: () => void;
  onBack: () => void;
  overlayStyle?: React.CSSProperties;
};

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({
  state, text, onTextChange, onSubmit, onCancel, onClose, onBack, overlayStyle,
}) => {
  const aiC = useAiColors();
  if (state === 'closed') return null;

  if (state === 'success') {
    return (
      <FeedbackOverlay style={overlayStyle}>
        <FeedbackResult>
          <FeedbackCloseBtn type="button" onClick={onClose} aria-label="Close">
            <Icon icon="x" size={18} color={aiC.main} />
          </FeedbackCloseBtn>
          <FeedbackTitleRow>
            <Icon icon={ICON_HAPPY} size={32} color={aiC.main} />
            <FeedbackResultTitle>Feedback received!</FeedbackResultTitle>
          </FeedbackTitleRow>
          <FeedbackLabel>
            Thank you for your contribution! We'll use it to improve the accuracy of the AI.
          </FeedbackLabel>
          <FeedbackButtonsRow>
            <FeedbackPrimaryBtn type="button" onClick={onClose}>Close</FeedbackPrimaryBtn>
          </FeedbackButtonsRow>
        </FeedbackResult>
      </FeedbackOverlay>
    );
  }

  if (state === 'error') {
    return (
      <FeedbackOverlay style={overlayStyle}>
        <FeedbackResult>
          <FeedbackCloseBtn type="button" onClick={onClose} aria-label="Close">
            <Icon icon="x" size={18} color={aiC.main} />
          </FeedbackCloseBtn>
          <FeedbackTitleRow>
            <Icon icon={ICON_SAD} size={32} color={aiC.main} />
            <FeedbackResultTitle>There was an error.</FeedbackResultTitle>
          </FeedbackTitleRow>
          <FeedbackLabel>
            It was not possible to send your feedback. Please try again later.
          </FeedbackLabel>
          <FeedbackButtonsRow>
            <FeedbackGhostBtn type="button" onClick={onBack}>Back</FeedbackGhostBtn>
            <FeedbackPrimaryBtn type="button" onClick={onClose}>Close</FeedbackPrimaryBtn>
          </FeedbackButtonsRow>
        </FeedbackResult>
      </FeedbackOverlay>
    );
  }

  // 'open' or 'sending'
  const isSending = state === 'sending';
  return (
    <FeedbackOverlay style={overlayStyle}>
      <FeedbackForm>
        <FeedbackCloseBtn type="button" onClick={onCancel} aria-label="Close">
          <Icon icon="x" size={18} color={aiC.main} />
        </FeedbackCloseBtn>
        <FeedbackLabel>Do you have any additional feedback?</FeedbackLabel>
        <FeedbackTextarea
          placeholder="Type your feedback here."
          value={text}
          onChange={e => onTextChange(e.target.value)}
          disabled={isSending}
        />
        <FeedbackButtonsRow>
          <FeedbackCancelBtn type="button" onClick={onCancel} disabled={isSending}>
            Cancel
          </FeedbackCancelBtn>
          <FeedbackPrimaryBtn type="button" onClick={onSubmit} disabled={isSending}>
            {isSending && <FeedbackSpinner />}
            Send feedback
          </FeedbackPrimaryBtn>
        </FeedbackButtonsRow>
      </FeedbackForm>
    </FeedbackOverlay>
  );
};

// ─── Aside ────────────────────────────────────────────────────────────────────
const AsideRoot = styled.aside<{ $open: boolean }>`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  width: 63px;
  margin-left: 24px;
  background: ${t('aiPanelBg', C_ASIDE_BG)};
  box-sizing: border-box;
  overflow: visible;
  position: static;
`;

// Sliding history panel — overlays main content from the right
const HistoryPanel = styled.div<{ $open: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 282px;
  height: 100%;
  background: ${t('aiPanelBg', C_ASIDE_BG)};
  display: flex;
  flex-direction: column;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.25s ease;
  overflow: hidden;
  box-shadow: ${({ $open }) => ($open ? '-4px 0 12px rgba(0,0,0,0.08)' : 'none')};
  z-index: 3;
`;

const HistorySearch = styled.div`
  padding: 21px 24px 0;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 13px;
  background: ${HEX_WHITE};
  border-radius: 5px;
  border: 1px solid ${t('aiPanelIconBtnBg', C_ICON_BTN_BG)};
  margin-top: 2rem;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 12px;
  font-weight: 400;
  font-family: 'Open Sans', sans-serif;
  color: ${C_HISTORY_TOPIC_DESC};
  flex: 1;
  background: transparent;
  &::placeholder { color: #B3B3B3; }
`;

const TopicsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  scrollbar-width: thin;
  scrollbar-color: ${C_SCROLL_THUMB} transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${C_SCROLL_THUMB}; border-radius: 2px; }
`;

const TopicCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: transparent;
  border: 1px solid ${C_HISTORY_TOPIC_BORDER};
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  &:hover { background: rgba(0,0,0,0.02); border-color: ${t('aiPanelMainColor', HEX_LOCHMARA)}; }
  &:active { background: rgba(0,0,0,0.04); }
`;

const TopicTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${t('aiLinkColor', C_HISTORY_TOPIC_LINK)};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TopicPreview = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${C_HISTORY_TOPIC_DESC};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TopicTime = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 4px;
`;

const TopicTimeText = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: ${C_HISTORY_TOPIC_TIME};
`;

// Clock icon (inline — no equivalent in icon set)
const ClockIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="5.5" stroke={C_GRAY_ICON} strokeWidth="1" />
    <path d="M7 4.5V7.5L9 9" stroke={C_GRAY_ICON} strokeWidth="1" strokeLinecap="round" />
  </svg>
);

// Aside buttons strip (right 63px)
const AsideButtons = styled.div`
  width: 63px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 16px 16px 16px 0;
  gap: 16px;
  box-sizing: border-box;
  background: ${t('aiPanelIconBtnBg', '#dfedf4')};
  position: relative;
  z-index: 4;
`;

const AsideIconButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 5px;
  border: none;
  background: ${(props: AnyProps & { $active?: boolean }) => (props.$active ? t('aiPanelMainColor', HEX_LOCHMARA)(props) : t('aiPanelIconBtnBg', C_ICON_BTN_BG)(props))};
  border-radius: 50%;
  cursor: pointer;
  box-sizing: border-box;
  &:hover { 
    background: ${t('aiPanelMainColor', HEX_LOCHMARA)};
    color: ${HEX_WHITE};
    > span {
      fill: ${HEX_WHITE};
      color: ${HEX_WHITE};
    }
  };

  &:hover svg path { fill: ${HEX_WHITE}; stroke: ${HEX_WHITE}; }
`;

// ─── Answer text rendering with [[N]] sources and **bold** ───────────────────

const normalizeForViewerHighlight = (str: string): string => {
  return str
    .replace(/[''ʼ`´]/g, "'")
    .replace(/[""]/g, '"')
    .replace(/[–—]/g, '-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[()\[\]{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getHighlightTextFromSource = (source: AiChatSource): string | null => {
  const page = source.pages?.[0] ?? null;
  if (page != null || !source.sourceText) return null;
  const normalized = normalizeForViewerHighlight(source.sourceText);
  const words = normalized.split(/\s+/).filter(Boolean);
  const startWords = words.slice(0, 10).join(' ');
  const endWords = words.slice(-10).join(' ');
  return words.length > 20 && startWords !== endWords
    ? `${startWords}|||${endWords}`
    : words.slice(0, 15).join(' ');
};

// Some model outputs use a literal "<br>" (or "<br/>") string to indicate a line break
// instead of an actual newline character — normalize those into real newlines.
const normalizeLineBreaks = (content: string): string =>
  content.replace(/<br\s*\/?\s*>/gi, '\n');

// Strip markdown/refs/br markers for a clean plain-text preview (used in the Q&A history list).
const stripFormattingForPreview = (content: string): string =>
  normalizeLineBreaks(content)
    .replace(/\[\[\d+\]\]/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^#{1,3}\s*/gm, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const renderAnswerContent = (
  content: string,
  sources: AiChatSource[] | undefined,
  onOpenSource: ((page: number | null, highlightText: string | null) => void) | undefined,
): React.ReactNode[] => {
  // Split by [[N]] references
  const parts = content.split(/(\[\[\d+\]\])/);

  // Group adjacent markers (separated only by empty/whitespace text) into a single chip,
  // matching the behavior used for the AI summary (e.g. "Page 1 +2").
  type TextItem = { type: 'text'; text: string; key: number };
  type RefGroupItem = { type: 'refGroup'; markers: number[]; key: number };
  const grouped: (TextItem | RefGroupItem)[] = [];
  let pendingMarkers: number[] = [];

  const flushMarkers = (key: number) => {
    if (pendingMarkers.length === 0) return;
    grouped.push({ type: 'refGroup', markers: pendingMarkers, key });
    pendingMarkers = [];
  };

  parts.forEach((part, i) => {
    const match = part.match(/^\[\[(\d+)\]\]$/);
    if (match) {
      pendingMarkers.push(Number(match[1]));
    } else if (part.trim() === '' && pendingMarkers.length > 0) {
      // whitespace between adjacent markers — keep grouping
    } else {
      flushMarkers(i);
      if (part) grouped.push({ type: 'text', text: part, key: i });
    }
  });
  flushMarkers(parts.length);

  const result: React.ReactNode[] = [];

  grouped.forEach((item, gi) => {
    if (item.type === 'text') {
      // Render bold (**text**) within text segments
      const boldParts = item.text.split(/(\*\*[^*]+\*\*)/);
      boldParts.forEach((bp, j) => {
        const boldMatch = bp.match(/^\*\*(.+)\*\*$/);
        if (boldMatch) {
          result.push(<strong key={`bold-${item.key}-${j}`}>{boldMatch[1]}</strong>);
        } else if (bp) {
          result.push(<React.Fragment key={`text-${item.key}-${j}`}>{bp}</React.Fragment>);
        }
      });
      return;
    }

    // refGroup — single AiSourceLink with multiple sources (shows "+N" badge)
    const matched = item.markers
      .map(n => sources?.find(s => s.n === n) || sources?.find(s => s.chunkIndex === n))
      .filter((s): s is AiChatSource => Boolean(s));

    // Check if the following text item starts with ". " → break after the chip
    const nextItem = grouped[gi + 1];
    const nextText = nextItem?.type === 'text' ? nextItem.text : '';
    const shouldBreak = /^\.\s/.test(nextText) || nextText === '.';

    if (matched.length === 0) {
      result.push(<span key={`ref-${item.key}`} style={{ fontSize: 10, color: 'rgba(0,141,208,0.9)' }}>[{item.markers.join(', ')}]</span>);
    } else {
      const label = matched[0].pages?.length ? `Page ${matched[0].pages.join(', ')}` : `Source ${item.markers[0]}`;
      const sourceEntries = matched.map(s => ({
        filename: s.pages?.length ? `Page ${s.pages.join(', ')}` : (s.pictureLabel || `Source ${s.n}`),
        excerpt: s.sourceText || s.chunk || '',
        imageUrl: s.imageUrl,
      }));
      result.push(
        <AiSourceLink
          key={`ref-${item.key}`}
          label={label}
          sources={sourceEntries}
          onOpen={onOpenSource ? (source) => {
            const idx = sourceEntries.indexOf(source);
            const matchedSource = matched[idx >= 0 ? idx : 0];
            const page = matchedSource.pages?.[0] ?? null;
            const highlight = getHighlightTextFromSource(matchedSource);
            onOpenSource(page, highlight);
          } : undefined}
        />
      );
    }

    if (shouldBreak && nextItem?.type === 'text') {
      // Consume the leading dot from the next text item and break
      nextItem.text = nextItem.text.slice(1);
      result.push(<React.Fragment key={`dot-${item.key}`}>.</React.Fragment>);
      result.push(<br key={`br-${item.key}`} />);
    }
  });

  return result;
};

// ─── ChatLog sub-component ────────────────────────────────────────────────────
type ChatLogProps = {
  messages: AiChatMessage[];
  isAnswering: boolean;
  recordClass?: RecordClass;
  fileExtension?: string;
  onRate?: (msgId: string, rating: 'up' | 'down') => void;
  messageSources?: Record<string, AiChatSource[]>;
  onOpenSource?: (page: number | null, highlightText: string | null) => void;
  onShowBottomChange?: (visible: boolean) => void;
  hideFeedbacks?: boolean;
};

const ChatLog: React.FC<ChatLogProps> = ({ messages, isAnswering, onRate, messageSources, onOpenSource, onShowBottomChange, hideFeedbacks = false }) => {
  const aiC = useAiColors();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showTop, setShowTop] = useState(false);
  const [ratings, setRatings] = useState<Record<string, 'up' | 'down' | null>>({});

  const checkGradients = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowTop(el.scrollTop > 8);
    onShowBottomChange?.(el.scrollTop + el.clientHeight < el.scrollHeight - 8);
  }, [onShowBottomChange]);

  useEffect(() => {
    checkGradients();
    const el = scrollRef.current;
    el?.addEventListener('scroll', checkGradients);
    return () => el?.removeEventListener('scroll', checkGradients);
  }, [checkGradients, messages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isAnswering]);

  return (
    <ChatScrollArea ref={scrollRef}>
      <TopFade $visible={showTop} />

      {messages.map((msg) =>
        msg.role === 'user' ? (
          <PromptWrap key={msg.id}>
            <PromptBubble>{msg.content}</PromptBubble>
          </PromptWrap>
        ) : (
          <AnswerWrap key={msg.id}>
            <AiStatusRow>
              {isAnswering && msg.content === '' ? (
                <SpinnerRing style={{ width: 14, height: 14 }} />
              ) : (
                <Icon icon={ICON_STARS_CIRCLE} size={16} color={C_GRAY_ICON} />
              )}
              <AiStatusText>
                {isAnswering && msg.content === '' ? (
                  <>Thinking<ThinkingDots /></>
                ) : (
                  'Done thinking.'
                )}
              </AiStatusText>
            </AiStatusRow>

            {msg.content !== '' && (
              <>
                <AnswerParasWrap>
                  {normalizeLineBreaks(msg.content).split('\n\n').map((text, i) => {
                    // Split further by single newlines (br) — a heading only spans until the next break
                    const lines = text.split('\n');
                    return lines.map((line, li) => {
                      const headingMatch = line.match(/^(#{1,3})\s*(.+)$/);
                      if (headingMatch) {
                        return (
                          <AnswerPara key={`${i}-${li}`} style={{ fontWeight: 700, fontSize: headingMatch[1].length === 1 ? 15 : 13 }}>
                            {renderAnswerContent(headingMatch[2], messageSources?.[msg.id], onOpenSource)}
                          </AnswerPara>
                        );
                      }
                      if (!line.trim()) return null;
                      return (
                        <AnswerPara key={`${i}-${li}`}>
                          {renderAnswerContent(line, messageSources?.[msg.id], onOpenSource)}
                        </AnswerPara>
                      );
                    });
                  })}
                </AnswerParasWrap>
                {!hideFeedbacks && (
                  <RatingRow>
                    <RatingLabel>Is it helpful?</RatingLabel>
                    <ThumbsRow>
                      <ThumbBtn
                        type="button"
                        aria-label="Thumbs up"
                        onClick={() => {
                          const next = ratings[msg.id] === 'up' ? null : 'up';
                          setRatings(r => ({ ...r, [msg.id]: next }));
                          if (next && onRate) onRate(msg.id, 'up');
                        }}
                        style={{ background: ratings[msg.id] === 'up' ? aiC.iconBtnBg : undefined }}
                        >
                          <Icon icon={ICON_THUMBS_UP} size={12} color={aiC.main} />
                      </ThumbBtn>

                      <ThumbBtn
                        type="button"
                        aria-label="Thumbs down"
                        onClick={() => {
                          const next = ratings[msg.id] === 'down' ? null : 'down';
                          setRatings(r => ({ ...r, [msg.id]: next }));
                          if (next && onRate) onRate(msg.id, 'down');
                        }}
                        style={{ background: ratings[msg.id] === 'down' ? aiC.iconBtnBg : undefined }}
                        >
                          <Icon icon={ICON_THUMBS_DOWN} size={12} color={aiC.main} />
                      </ThumbBtn>
                    </ThumbsRow>
                  </RatingRow>
                )}
              </>
            )}
          </AnswerWrap>
        )
      )}

    </ChatScrollArea>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
export const AiPanelEV: React.FC<AiPanelEVProps> = ({
  initialState,
  recordFileName = MOCK_RECORD_FILE,
  recordClass,
  fileExtension,
  onClose,
  onSendMessage,
  onOpenSource,
  processingLabel,
  hideFeedbacks = false,
  className,
}) => {
  const aiC = useAiColors();
  const [panelState, setPanelState] = useState<AiPanelEVState>(initialState ?? 'loading');
  const [inputValue, setInputValue] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [messageSources, setMessageSources] = useState<Record<string, AiChatSource[]>>({});
  const [qaHistory, setQaHistory] = useState<{ id: string; question: string; answer: string; sources?: AiChatSource[]; timestamp: string }[]>([]);
  const [feedbackState, setFeedbackState] = useState<FeedbackPanelState>('closed');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<'up' | 'down' | null>(null);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close history panel when clicking outside the entire panel
  useEffect(() => {
    if (!historyOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [historyOpen]);

  // Auto-advance: loading → initial (from prop change or timeout fallback)
  useEffect(() => {
    if (initialState === 'initial' && panelState === 'loading') {
      setPanelState('initial');
    }
  }, [initialState]);

  // Submit: add messages → answering → answered
  const handleSubmit = (value: string) => {
    if (!value.trim()) return;
    const userId = `u-${Date.now()}`;
    const aiId   = `a-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      { role: 'user',      content: value, id: userId },
      { role: 'assistant', content: '',    id: aiId   },
    ]);
    setInputValue('');
    setPanelState('answering');

    let accumulated = '';
    let capturedSources: AiChatSource[] | undefined;

    const saveToHistory = () => {
      if (!accumulated) return;
      setQaHistory(prev => [{
        id: `qa-${Date.now()}`,
        question: value,
        answer: accumulated,
        sources: capturedSources,
        timestamp: 'Just now',
      }, ...prev]);
    };

    if (onSendMessage) {
      onSendMessage(
        value,
        (chunk) => {
          accumulated += chunk;
          setMessages(prev =>
            prev.map(m => m.id === aiId ? { ...m, content: m.content + chunk } : m)
          );
        },
        () => {
          setPanelState('answered');
          saveToHistory();
        },
        () => {
          setMessages(prev =>
            prev.map(m => m.id === aiId
              ? { ...m, content: m.content || 'Sorry, something went wrong. Please try again.' }
              : m
            )
          );
          setPanelState('answered');
        },
        (sources) => {
          capturedSources = sources;
          setMessageSources(prev => ({ ...prev, [aiId]: sources }));
        },
      );
    } else {
      // Fallback mock for demo/storybook
      setTimeout(() => {
        accumulated = MOCK_ANSWER;
        setMessages(prev =>
          prev.map(m => m.id === aiId ? { ...m, content: MOCK_ANSWER } : m)
        );
        setPanelState('answered');
        saveToHistory();
      }, 2000);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setMessageSources({});
    setInputValue('');
    setPanelState('initial');
    setFeedbackState('closed');
  };

  const handleLoadHistory = (entry: { id: string; question: string; answer: string; sources?: AiChatSource[] }) => {
    const aiId = `${entry.id}-a`;
    setMessages([
      { id: `${entry.id}-u`, role: 'user', content: entry.question },
      { id: aiId, role: 'assistant', content: entry.answer },
    ]);
    setMessageSources(entry.sources ? { [aiId]: entry.sources } : {});
    setInputValue('');
    setPanelState('answered');
    setHistoryOpen(false);
    setHistorySearch('');
  };

  const handleRate = (_msgId: string, rating: 'up' | 'down') => {
    if (hideFeedbacks) return;
    setFeedbackRating(rating);
    setFeedbackText('');
    setFeedbackState('open');
  };

  const handleFeedbackSubmit = () => {
    setFeedbackState('sending');
    // Mock: 50% success / 50% error for demo purposes
    setTimeout(() => {
      // setFeedbackState(Math.random() >= 0.5 ? 'success' : 'error');
      setFeedbackState('success');
    }, 1500);
  };

  const handleFeedbackClose = () => {
    setFeedbackState('closed');
    setFeedbackText('');
    setFeedbackRating(null);
  };

  const handleFeedbackBack = () => {
    setFeedbackState('open');
  };

  const filteredHistory = qaHistory.filter(entry =>
    !historySearch.trim() ||
    entry.question.toLowerCase().includes(historySearch.toLowerCase()) ||
    entry.answer.toLowerCase().includes(historySearch.toLowerCase())
  );

  const isChatMode = panelState === 'answering' || panelState === 'answered';
  const isAnswering = panelState === 'answering';
  const showTitle   = panelState === 'initial' || isChatMode;
  // ponytail: show aside when in chat mode OR when returning to initial with existing history
  const showAside   = isChatMode || (panelState === 'initial' && qaHistory.length > 0);

  return (
    <Anchor.PortalContainer>
      <Root className={className} ref={rootRef}>

        {/* ── Close button with tooltip ── */}
        {!historyOpen && <CloseButtonTrigger
          $lineHeight={1}
          $asideVisible={showAside && !historyOpen}
          tooltip={<Tooltip my="top center" at="bottom center">Close</Tooltip>}
        >
          <CloseButton type="button" onClick={onClose} aria-label="Close AI panel">
            <Icon icon="x" size={20} color={aiC.main} />
          </CloseButton>
        </CloseButtonTrigger>}

        {/* ── Main column ── */}
      <Main $chatMode={isChatMode || showAside} 
        $paddingTop={
          panelState === 'initial' ? '15rem' :
          panelState === 'loading' ? '24rem' :
          panelState === 'error'   ? '24rem' :
          '24px'
        }
        onClick={() => historyOpen && setHistoryOpen(false)}
        // $verticallyCentered={panelState === 'initial' || panelState === 'loading' || panelState === 'error'} 
        >

          {/* Title row */}
          {showTitle && (
            <Title>
              <Icon icon="stars-chat" size={24} color={"#999999"} />
              <TitleText>AI Q&A</TitleText>
            </Title>
          )}

          {/* Record badge in chat mode */}
          {isChatMode && recordFileName && (
            <RecordFileBadge recordFileName={recordFileName} recordClass={recordClass} fileExtension={fileExtension} />
          )}

          {/* Loading */}
          {panelState === 'loading' && (
            <StatusWrap>
              <SpinnerRing />
              <StatusText>{processingLabel || 'Loading.'}<br />Please wait.</StatusText>
            </StatusWrap>
          )}

          {/* Error */}
          {panelState === 'error' && (
            <StatusWrap>
              {/* <ErrorIcon /> */}
              <Icon icon={ICON_ERROR_HEX} size={30} color="#999999" />
              <StatusText>Could not load the AI engine.<br />Please try again later.</StatusText>
            </StatusWrap>
          )}

          {/* Initial */}
          {panelState === 'initial' && (
            <ContentArea>
              <AiChatInput
                recordFileName={recordFileName}
                value={inputValue}
                onChange={setInputValue}
                onSubmit={handleSubmit}
              />
              <button
                type="button"
                onClick={() => handleSubmit(inputValue)}
                disabled={!inputValue.trim() || inputValue.length >= 20000}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: aiC.main,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '5px',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'Open Sans', sans-serif",
                  cursor: inputValue.trim() && inputValue.length < 20000 ? 'pointer' : 'default',
                  opacity: inputValue.trim() && inputValue.length < 20000 ? 1 : 0.5,
                }}
              >
                Ask Question
              </button>
            </ContentArea>
          )}

          {/* Chat (answering / answered) */}
          {isChatMode && (
            <ChatContent>
              <ChatLog
                messages={messages}
                isAnswering={isAnswering}
                recordClass={recordClass}
                fileExtension={fileExtension}
                onRate={handleRate}
                messageSources={messageSources}
                onOpenSource={onOpenSource}
                onShowBottomChange={setShowBottomFade}
                hideFeedbacks={hideFeedbacks}
              />
              <BottomFade $visible={showBottomFade} />
              <ChatInputWrap>
                <button
                  type="button"
                  onClick={handleNewChat}
                  disabled={isAnswering}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: aiC.main,
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '5px',
                    fontSize: '13px',
                    fontWeight: 600,
                    fontFamily: "'Open Sans', sans-serif",
                    cursor: isAnswering ? 'default' : 'pointer',
                    opacity: isAnswering ? 0.5 : 1,
                  }}
                >
                  Ask New Question
                </button>
              </ChatInputWrap>
            </ChatContent>
          )}

        </Main>

        {/* ── Aside ── */}
        {showAside && <AsideRoot $open={historyOpen}>

          {/* Sliding history panel */}
          <HistoryPanel $open={historyOpen} style={{ boxShadow: 'none' }}>
            <HistorySearch>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Title>
                  <Icon icon="history" size={24} color={aiC.main} />
                  <TitleText>Q&A History</TitleText>
                </Title>
                <CloseButton type="button" onClick={() => setHistoryOpen(false)} aria-label="Close history">
                  <Icon icon="x" size={18} color={aiC.main} />
                </CloseButton>
              </div>
              <SearchBox>
                <Icon icon="search" size={16} color={HEX_GRAY_60} />
                <SearchInput
                  type="text"
                  placeholder="Search questions"
                  value={historySearch}
                  onChange={e => setHistorySearch(e.target.value)}
                />
              </SearchBox>
            </HistorySearch>
            <TopicsList>
              {filteredHistory.length === 0 && (
                <TopicTimeText style={{ textAlign: 'center', paddingTop: 24 }}>No questions yet</TopicTimeText>
              )}
              {filteredHistory.map(entry => (
                <TopicCard key={entry.id} type="button" onClick={() => handleLoadHistory(entry)}>
                  <TopicTitle>{entry.question}</TopicTitle>
                  <TopicPreview>{(() => { const p = stripFormattingForPreview(entry.answer); return p.slice(0, 120) + (p.length > 120 ? '…' : ''); })()}</TopicPreview>
                  <TopicTime>
                    <ClockIcon />
                    <TopicTimeText>{entry.timestamp}</TopicTimeText>
                  </TopicTime>
                </TopicCard>
              ))}
            </TopicsList>
          </HistoryPanel>

          {/* Buttons strip */}
          {!historyOpen && <AsideButtons>
            <Tooltip.Trigger
              $lineHeight={1}
              tooltip={<Tooltip my="top right" at="top left" margin="0 6px 0 0">Q&A History</Tooltip>}
            >
              <AsideIconButton
                type="button"
                $active={historyOpen}
                onClick={() => { setHistorySearch(''); setHistoryOpen(true); }}
                aria-label="Q&A History"
              >
                <Icon icon="history" size={20} color={historyOpen ? HEX_WHITE : aiC.main} />
              </AsideIconButton>
            </Tooltip.Trigger>
          </AsideButtons>}

        </AsideRoot>}

        {/* ── Feedback Panel ── */}
        {!hideFeedbacks && (
          <FeedbackPanel
            rating={feedbackRating}
            state={feedbackState}
            text={feedbackText}
            onTextChange={setFeedbackText}
            onSubmit={handleFeedbackSubmit}
            onCancel={handleFeedbackClose}
            onClose={handleFeedbackClose}
            onBack={handleFeedbackBack}
          />
        )}

      </Root>
    </Anchor.PortalContainer>
  );
};

export default AiPanelEV;
